import axios from "axios";
import { BASE_URL } from "../constants/APIConstants";
import { authConfig } from "../api/http";

/**
 * Calls the backend proxy to the JW Player management API (admin only).
 * `payload` is `{ body, url, method, jw_tag }`.
 * Resolves with the raw axios response (`response.data.data` holds the JW result).
 */
export function jwplayerRequest(payload) {
  return axios.post(`${BASE_URL}/api/admin/jwplayer-api`, payload, authConfig());
}

/**
 * Uploads a file to a JW Player direct upload link.
 * Resolves `true` when JW answers 200, `false` otherwise; rejects on network errors.
 */
export async function uploadFileToLink(uploadLink, file, fileType) {
  const response = await fetch(uploadLink, {
    method: "PUT",
    body: file,
    timeout: 900000,
    headers: {
      "Content-Type": fileType,
      "Access-Control-Allow-Origin": "*",
    },
  });
  return response.status === 200;
}

/**
 * Same as `uploadFileToLink` but with axios and a 20 min timeout; never rejects
 * (resolves `false` on any error).
 */
export async function tryUploadFileToLink(uploadLink, file, fileType) {
  try {
    const response = await axios.put(uploadLink, file, {
      headers: { "Content-Type": fileType },
      timeout: 1200000,
    });
    return response.status === 200;
  } catch (error) {
    console.error("Error uploading video:", error);
    return false;
  }
}

// Multipart uploads, talking to JW Player directly with the upload token.

/** Upload links of the parts of a multipart upload, or `undefined` on error. */
export async function getUploadParts(uploadToken, uploadId, totalParts) {
  try {
    const response = await axios.get(
      `https://api.jwplayer.com/v2/uploads/${uploadId}/parts`,
      {
        headers: {
          Authorization: `Bearer ${uploadToken}`,
          "Content-Type": "application/json",
        },
        params: { page: 1, page_length: totalParts },
      }
    );
    return response.data.parts;
  } catch (error) {
    return undefined;
  }
}

/** Uploads one part; `onProgress` receives the part's completion percentage. Never rejects. */
export async function uploadFilePart(uploadLink, chunk, onProgress) {
  try {
    await axios.put(uploadLink, chunk, {
      headers: { "Content-Type": "" },
      onUploadProgress: ({ loaded, total }) => {
        onProgress(Math.floor((loaded * 100) / total));
      },
    });
  } catch (error) {
    console.error("Error uploading video:", error);
    return false;
  }
}

/** Resolves with JW's response body, or with the error itself on failure. */
export async function completeMultipartUpload(uploadId, uploadToken) {
  try {
    const response = await axios.put(
      `https://api.jwplayer.com/v2/uploads/${uploadId}/complete`,
      {},
      {
        headers: {
          Authorization: `Bearer ${uploadToken}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    return error;
  }
}

/** Media details (e.g. `duration`) through the backend proxy. Resolves with the raw axios response. */
export function getJwMediaInfo(mediaId) {
  return axios.get(
    `${BASE_URL}/api/admin/get-info-jwplayer-api/${mediaId}`,
    authConfig()
  );
}
